import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PaymentService from '../../services/paymentService';

const PaymentSuccess = () => {
  const { user, refreshUserProfile } = useAuth(); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // ✅ ADD: Check if user is authenticated first
    if (!user) {
      setError('Please log in to verify payment');
      setVerifying(false);
      navigate('/login', { state: { from: '/payment-success', order_id: searchParams.get('order_id') } });
      return;
    }

    const orderId = searchParams.get('order_id');
    if (!orderId) {
      setError('No order ID found');
      setVerifying(false);
      return;
    }

    verifyPayment(orderId);
  }, [searchParams, user, navigate]); // ✅ ADD: user and navigate to dependencies

  const verifyPayment = async (orderId) => {
    // ✅ ADD: Defensive check (in case user logs out during verification)
    if (!user) {
      setError('Please log in to verify payment');
      setVerifying(false);
      navigate('/login', { state: { from: '/payment-success', order_id } });
      return;
    }

    try {
      const verification = await PaymentService.verifyPayment(orderId);
      
      if (verification.order_status === 'PAID') {
        // Update user tier
        
        await refreshUserProfile();
        setPaymentStatus('success');
      } else {
        setPaymentStatus('failed');
        setError('Payment verification failed. Please contact support if payment was deducted.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setPaymentStatus('error');
      setError(err.message || 'Failed to verify payment. Please contact support.');
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10">
            <Icon name="Check" size={40} className="text-success" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground">
              Your account has been upgraded to Premium. You now have access to all premium features.
            </p>
          </div>
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={() => navigate('/user-dashboard')}
            iconName="ArrowRight"
            iconPosition="right"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-error/10">
          <Icon name="X" size={40} className="text-error" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Payment Verification Failed</h1>
          <p className="text-muted-foreground">
            {error || 'We could not verify your payment. Please contact support if payment was deducted.'}
          </p>
        </div>
        <div className="space-y-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={() => navigate('/upgrade')}
          >
            Try Again
          </Button>
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => navigate('/user-dashboard')}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;